import axios from 'axios';
import { THERAPIST_AID_BRANDING, USER_BRANDING, CUSTOM_BRANDING, CUSTOM_MODAL, NONE_BRANDING } from './constants.js';
import { updateBrandingReference, setLogo, onFailure } from './services';
import { captureException } from '@sentry/browser';

import 'cropperjs';

export const UploadLogo = ({ endpoint }) => {
    return {
        open: false,

        uploadState: {
            loading: false,
            fileName: '',
            error: false,
            progress: 0,
        },

        source: null,

        init() {
            this.$watch('open', (value) => {
                if (value) {
                    this.uploadState = {
                        loading: false,
                        fileName: '',
                        error: false,
                        progress: 0,
                    };

                    this.$dispatch('disable-scroll');
                    return;
                }

                this.$dispatch('enable-scroll');
            });
        },

        drop(event, input) {
            const items = event.dataTransfer?.items;
            if (!items || items.length === 0) return;

            const fileItem = [...items].find(item => item.kind === 'file');
            if (!fileItem) return;

            const file = fileItem.getAsFile();
            if (!file) return;

            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(file);

            input.files = dataTransfer.files;

            input.dispatchEvent(new Event('change', { bubbles: true }));
        },

        async upload(file) {
            if (!file) return;

            this.uploadState = {
                loading: true,
                fileName: file.name,
                error: false,
                progress: 0,
            };

            if (['image/svg+xml', 'image/webp'].includes(file.type)) {
                const extension = file.type === 'image/svg+xml' ? 'svg' : 'webp';

                try {
                    file = await this.convertToPng(file, extension);
                } catch (error) {
                    this.onUploadFailure(`${extension.toUpperCase()} conversion failed. Try a PNG or JPG.`);

                    return;
                }
            }

            if (file.size > 10 * 1024 * 1024) {
                this.uploadState.error = 'The logo may not be greater than 10MB.';
                this.uploadState.loading = false;
                return;
            }

            const form = new FormData();
            form.append('logo', file);

            this.source = axios.CancelToken.source();

            try {
                const { data: { url } } = await axios.post(endpoint, form, {
                    cancelToken: this.source.token,
                    withCredentials: true,
                    withXSRFToken: true,
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (progressEvent) => {
                        if (progressEvent.lengthComputable) {
                            this.uploadState.progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                        }
                    },
                });

                setLogo(this);
                this.open = false;

                const data = { url, coordinates: null, size: CUSTOM_MODAL.DEFAULT_LOGO_SIZE };

                this.$dispatch('show-customize-logo-modal', data);
                this.$dispatch('account-settings-set-logo', data);
                this.$dispatch('updated-logo', null);
            } catch (exception) {
                if (exception instanceof axios.Cancel) return;

                this.onUploadFailure(exception)

                console.error(exception);
            } finally {
                this.uploadState.loading = false;

                typeof this.updateDefaultLogoOption === 'function' && this.updateDefaultLogoOption(NONE_BRANDING.option);
                typeof this.setBrandingPreference === 'function' && this.$nextTick(() => this.setBrandingPreference(NONE_BRANDING.option));
            }
        },

        onUploadFailure(exception) {
            const { response } = exception;

            this.uploadState.loading = false;

            if (typeof response === 'object' && response?.status === 422) {
                return this.uploadState.error = response.data.errors.logo[0];
            }

            if (typeof response === 'object' && response?.status === 403) {
                return this.uploadState.error = 'You do not have permission to upload logo.';
            }

            if (typeof exception === 'string') {
                return this.uploadState.error = exception;
            }

            captureException(exception);

            this.uploadState.error = 'Please try again, or later.';
        },

        handleCancel() {
            this.source?.cancel();

            this.uploadState = {
                loading: false,
                fileName: '',
                error: false,
                progress: 0,
            };
        },

        async convertToPng(imageFile, extension) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (event) => {
                    const img = new Image();

                    img.onload = () => {
                        const canvas = document.createElement('canvas');
                        canvas.width = img.width;
                        canvas.height = img.height;
                        const ctx = canvas.getContext('2d');

                        ctx.drawImage(img, 0, 0);

                        canvas.toBlob((blob) => {
                            if (!blob) reject(new Error(`Failed to convert ${extension.toUpperCase()} to PNG.`))

                            resolve(
                                new File([blob], imageFile.name.replace(new RegExp(`\\.${extension}$`, 'i'), '.png'), { type: 'image/png' })
                            );
                        }, 'image/png', 1.0);
                    };
                    img.onerror = () => reject(new Error(`Failed to load ${extension.toUpperCase()} image.`));
                    img.src = event.target.result;
                };
                reader.onerror = () => reject(new Error(`Failed to read ${extension.toUpperCase()} file.`));
                reader.readAsDataURL(imageFile);
            });
        },
    };
};

export const CustomizeLogo = ({ endpoint, cropperDebug = false }) => {
    window.CUSTOM_MODAL_CONSTANTS = CUSTOM_MODAL;

    const CROPPER_ASPECT_RATIO = 363.95 / 123.77;
    const CROPPER_WIDTH_PERCENTAGE = 363.95 / 390;

    const calcCropperWidth = () => Math.round(Math.min(window.innerWidth * CROPPER_WIDTH_PERCENTAGE, 576) * 100) / 100;
    const calcCropperHeight = (w) => Math.round((w / CROPPER_ASPECT_RATIO) * 100) / 100;

    return {
        open: false,
        width: window.innerWidth,

        cropperWidth: calcCropperWidth(),
        cropperHeight: calcCropperHeight(calcCropperWidth()),

        updateCropperDimensions() {
            this.cropperWidth = calcCropperWidth();
            this.cropperHeight = calcCropperHeight(this.cropperWidth);
        },

        logoSize: CUSTOM_MODAL.DEFAULT_LOGO_SIZE,
        currentSize: CUSTOM_MODAL.DEFAULT_LOGO_SIZE,
        baseFitScale: CUSTOM_MODAL.DEFAULT_LOGO_SIZE,

        logoPreviewUrl: null,
        url: null,
        coordinates: null,

        image: { naturalWidth: 0, naturalHeight: 0 },
        loading: false,
        source: null,
        suppressSync: false,
        canvasReady: true,
        previewDebounceTimer: null,

        get canvas() {
            return this.width >= 1280
                ? (this.$refs.mdCanvas || this.$refs.smCanvas)
                : this.$refs.smCanvas;
        },

        init() {
            const ready = () => {
                return new Promise((resolve) => {
                    if (!this.open || !this.url || !this.canvas) {
                        resolve();
                        return;
                    }

                    const cropperImage = this.canvas.querySelector('cropper-image');

                    if (!cropperImage) {
                        resolve();

                        return;
                    }

                    cropperImage.$ready(async (image) => {
                        this.image = { naturalWidth: image.naturalWidth, naturalHeight: image.naturalHeight };

                        await this.imageShrinker();

                        const syncedState = window.logoSyncState?.url === this.url
                            ? window.logoSyncState.editorState
                            : null;

                        this.coordinates = syncedState ?? this.coordinates;
                        this.coordinates && this.setCoordinates();
                        this.updatePreview();

                        if (!window.logoSyncState || window.logoSyncState.url !== this.url) {
                            window.logoSyncState = { editorState: this.extractEditorState(), url: this.url };
                        }

                        const cropperSelection = this.canvas.querySelector('cropper-selection');

                        cropperImage.addEventListener('transform', () => {
                            this.updatePreview();

                            if (!this.suppressSync) {
                                window.logoSyncState = { editorState: this.extractEditorState(), url: this.url };
                                this.sendSyncPositionEvent();
                            }
                        });

                        cropperSelection.addEventListener('change', () => {
                            this.updatePreview();
                        });

                        this.removeWheelListener();
                        resolve();
                    });
                });
            };

            this.$nextTick(() => ready());

            this.$watch('url', (newUrl) => {
                this.image = { naturalWidth: 0, naturalHeight: 0 };
                this.logoPreviewUrl = null;

                if (!newUrl) return;

                this.canvasReady = false;
                this.$nextTick(() => { this.canvasReady = true; });
            });

            this.$watch('canvasReady', (value) => {
                value && this.$nextTick(() => ready());
            });

            const onPositionSynced = () => {
                if (this.suppressSync) return;
                if (!this.open || !this.url || !this.image.naturalWidth) return;
                if (window.logoSyncState?.url !== this.url) return;

                this.coordinates = window.logoSyncState.editorState;

                this.setCoordinates();
                this.updatePreview();
            };

            this.$watch('open', (open) => {
                if (open) {
                    window.addEventListener('logo-position-synced', onPositionSynced);
                    setTimeout(() => this.$dispatch('disable-scroll'), 200);

                    return;
                }

                window.removeEventListener('logo-position-synced', onPositionSynced);

                window.logoSyncState = null;
                this.url = null;

                this.$dispatch('enable-scroll');
                window.dispatchEvent(new CustomEvent('customize-logo-closed'));
            });

            this.$watch('width', (newWidth, oldWidth) => {
                this.updateCropperDimensions();

                const wasLarge = oldWidth >= 1280;
                const isLarge = newWidth >= 1280;

                this.sendSyncPositionEvent();

                if (wasLarge !== isLarge && this.open && this.url && this.image.naturalWidth > 0) {
                    const previousCanvas = wasLarge ? (this.$refs.mdCanvas || this.$refs.smCanvas) : this.$refs.smCanvas;
                    const nextCanvas = isLarge ? (this.$refs.mdCanvas || this.$refs.smCanvas) : this.$refs.smCanvas;

                    if (previousCanvas === nextCanvas) return;

                    this.coordinates = this.extractEditorState(previousCanvas);
                    this.$nextTick(() => ready());

                    this.sendSyncPositionEvent();

                    return;
                }

                const wasMobile = oldWidth < 744;
                const nowMobile = newWidth < 744;

                if (wasMobile === nowMobile || !this.open || !this.url || !this.image.naturalWidth) return;

                if (window.logoSyncState?.url === this.url) {
                    this.coordinates = window.logoSyncState.editorState;

                    this.coordinates && this.setCoordinates();

                    this.sendSyncPositionEvent();
                }

                this.sendSyncPositionEvent();
            });
        },

        sendSyncPositionEvent() {
            window.dispatchEvent(new CustomEvent('logo-position-synced'));
        },

        updatePreview() {
            clearTimeout(this.previewDebounceTimer);
            this.previewDebounceTimer = setTimeout(async () => {
                if (!this.canvas) return;

                try {
                    const selection = this.canvas.querySelector('cropper-selection');
                    const previewCanvas = await selection.$toCanvas({ width: 576, height: 192 });
                    this.logoPreviewUrl = previewCanvas.toDataURL();
                    this.$dispatch('logo-preview-updated', { previewUrl: this.logoPreviewUrl });
                } catch (error) {
                    console.error('Failed to update preview:', error);
                }
            }, 150);
        },

        async sizeChanged(targetSize) {
            return new Promise((resolve) => {
                const target = Math.round(parseFloat(targetSize) * 10) / 10;
                const image = this.canvas.querySelector('cropper-image');
                const [currentScale, , , , translateX, translateY] = image.$getTransform();

                const newScale = this.baseFitScale * Math.pow(1.5, target);
                const relativeScale = newScale / currentScale;

                this.suppressSync = true;
                this.currentSize = target;
                this.canvas.querySelector('cropper-image').$scale(relativeScale);
                this.suppressSync = false;

                if (window.logoSyncState?.url === this.url) {
                    window.logoSyncState = { editorState: this.extractEditorState(), url: this.url };
                    this.sendSyncPositionEvent();
                }

                requestAnimationFrame(() => {
                    this.updatePreview();
                    resolve();
                });
            });
        },

        imageShrinker() {
            return new Promise((resolve) => {
                const selection = this.canvas.querySelector('cropper-selection');

                const selectionWidth = parseFloat(selection.getAttribute('width'));
                const selectionHeight = parseFloat(selection.getAttribute('height'));

                const scaleX = selectionWidth / this.image.naturalWidth;
                const scaleY = selectionHeight / this.image.naturalHeight;
                const fitScale = Math.min(scaleX, scaleY);

                const calculateDistortionBasedOnFitScale = (dimension) => (fitScale - 1) * (dimension / 2)

                const normalizedDistortionX = calculateDistortionBasedOnFitScale(this.image.naturalWidth);
                const normalizedDistortionY = calculateDistortionBasedOnFitScale(this.image.naturalHeight);

                const offsetX = selectionWidth - this.image.naturalWidth - normalizedDistortionX - 2;
                const offsetY = normalizedDistortionY

                this.suppressSync = true;

                this.canvas.querySelector('cropper-image').$setTransform([
                    fitScale, 0, 0, fitScale, offsetX, offsetY
                ]);

                this.suppressSync = false;

                this.baseFitScale = fitScale;
                this.currentSize = this.logoSize;

                requestAnimationFrame(() => resolve());
            });
        },

        removeWheelListener() {
            if (this.canvas.$onWheel) {
                this.canvas.removeEventListener('wheel', this.canvas.$onWheel, { capture: true });
                this.canvas.$onWheel = null;
            }
        },

        extractEditorState(canvas = this.canvas) {
            const cropperImage = canvas.querySelector('cropper-image');
            const cropperSelection = canvas.querySelector('cropper-selection');
            const [scale, , , , translateX, translateY] = cropperImage.$getTransform();
            const selectionWidth = parseFloat(cropperSelection.getAttribute('width'));
            const selectionHeight = parseFloat(cropperSelection.getAttribute('height'));
            const naturalWidth = this.image.naturalWidth;
            const naturalHeight = this.image.naturalHeight;

            const cropLeft = (naturalWidth * (scale - 1) / 2 - translateX) / scale;
            const cropTop = (naturalHeight * (scale - 1) / 2 - translateY) / scale;
            const cropWidth = selectionWidth / scale;
            const cropHeight = selectionHeight / scale;

            return `${cropLeft},${cropTop},${cropWidth},${cropHeight}`;
        },

        extractFullEditorState(canvas = this.canvas) {
            const cropperImage = canvas.querySelector('cropper-image');
            const [scale, skewX, skewY, scaleY, translateX, translateY] = cropperImage.$getTransform();

            return `${scale},${skewX},${skewY},${scaleY},${translateX},${translateY}`;
        },

        setCoordinates(preserveLogoSize = false) {
            const savedValues = this.coordinates.split(',').map(Number);
            const cropperImage = this.canvas.querySelector('cropper-image');

            if (savedValues.length === 6) {
                this.suppressSync = true;

                cropperImage.$setTransform(savedValues);

                this.suppressSync = false;

                return;
            }

            const [cropLeft, cropTop, cropWidth] = savedValues;
            const cropperSelection = this.canvas.querySelector('cropper-selection');
            const selectionWidth = parseFloat(cropperSelection.getAttribute('width'));
            const naturalWidth = this.image.naturalWidth;
            const naturalHeight = this.image.naturalHeight;

            const scale = selectionWidth / cropWidth;
            const translateX = naturalWidth / 2 * (scale - 1) - scale * cropLeft;
            const translateY = naturalHeight / 2 * (scale - 1) - scale * cropTop;

            this.suppressSync = true;

            cropperImage.$setTransform([scale, 0, 0, scale, translateX, translateY]);

            this.suppressSync = false;

            this.currentSize = Math.log(scale / this.baseFitScale) / Math.log(1.5);

            if (!preserveLogoSize) {
                this.logoSize = this.currentSize;
            }
        },

        handleUploadNewLogo() {
            this.open = false;
            this.$dispatch('show-upload-logo-modal');
        },

        async handleSave() {
            const selection = this.canvas.querySelector('cropper-selection');
            const rawCanvas = await selection.$toCanvas({ width: 576, height: 192 });

            const normalizedCanvas = document.createElement('canvas');
            normalizedCanvas.width = 576;
            normalizedCanvas.height = 192;
            normalizedCanvas.getContext('2d').drawImage(rawCanvas, 0, 0, 576, 192);

            const editorState = this.extractEditorState();
            const image = this.canvas.querySelector('cropper-image');
            const rawTransform = image.$getTransform();

            if (cropperDebug) {
                this.$refs.cropperDebug.style.display = '';
                this.$refs.cropperDebug.innerHTML = `
                    <div class="flex flex-col items-center justify-center py-4">
                        <p class="text-body-10 font-medium text-support-red text-center">DEBUG MODE</p>
                        <p class="text-body-12 text-center">editor_state: ${editorState}</p>
                        <p class="text-body-12 text-center">coordinates (legacy): ${rawTransform}</p>
                        <p class="text-body-12 text-center">Dimensions: ${normalizedCanvas.width} x ${normalizedCanvas.height}</p>
                    </div>
                `;
                this.$refs.cropperDebug.appendChild(normalizedCanvas);

                return;
            }

            this.source = axios.CancelToken.source();

            try {
                this.loading = true;

                let extension = 'png';
                const match = image.src.match(/\.([a-zA-Z0-9]+)(?:\?|#|$)/);
                if (match && match[1]) {
                    extension = match[1].toLowerCase();
                }

                const form = new FormData();
                const blob = await new Promise((resolve) => normalizedCanvas.toBlob(resolve, `image/${extension}`));
                const resized = new File([blob], `resized.${extension}`);

                form.append('logo', resized);
                form.append('editor_state', editorState);
                form.append('coordinates', rawTransform.toString());
                form.append('size', this.currentSize);
                form.append('_method', 'PUT')

                const { data: { resized: resizedLogoUrl } } = await axios.post(endpoint, form, {
                    cancelToken: this.source.token,
                    withCredentials: true,
                    withXSRFToken: true,
                    headers: { 'Content-Type': 'multipart/form-data' },
                });

                this.open = false;
                const meta = {
                    coordinates: editorState,
                    editor_state: editorState,
                    url: this.url,
                    size: this.currentSize,
                };

                setLogo(this, { logo: USER_BRANDING.label, meta });

                this.$dispatch('show-logo-prompt-modal', resizedLogoUrl);
                this.$dispatch('updated-logo', resizedLogoUrl);
                this.$dispatch('account-settings-set-resized', { logo: meta, resized: resizedLogoUrl });
            } catch (exception) {
                if (exception instanceof axios.Cancel) return;

                const message = onFailure({
                    exception,
                    defaultMessage: 'Something went wrong while setting your logo as default. Please try again later.'
                });

                this.$dispatch('notify', {
                    title: 'Oops!',
                    text: message,
                    type: 'warning',
                    action: () => this.$dispatch('show-upload-logo-modal'),
                    actionText: 'Try again'
                });

                this.open = false;
            } finally {
                this.loading = false;

                typeof this.refreshLogoOptions === 'function' && this.refreshLogoOptions();
            }
        },

        handleCancel() {
            this.source?.cancel();
            this.loading = false;
            this.open = false;
        },
    };
};

export const LogoPromptModal = () => {
    return {
        open: false,
        loading: false,
        width: window.innerWidth,
        url: null,

        async setPreference(preference) {
            try {
                this.loading = true;
                await updateBrandingReference(preference);

                typeof this.setBrandingPreference === 'function' && this.$dispatch('notify', { text: 'Preference successfully saved!', type: 'info' });
            } catch (exception) {
                const message = onFailure({ exception });

                this.$dispatch('notify', {
                    title: 'Oops!',
                    text: message,
                    type: 'warning',
                    action: () => this.open = true,
                    actionText: 'Try again'
                });
            } finally {
                this.loading = false;
                this.open = false;

                typeof this.setBrandingPreference === 'function' && this.setBrandingPreference(preference || THERAPIST_AID_BRANDING.option);
                typeof this.updateDefaultLogoOption === 'function' && this.updateDefaultLogoOption(preference || THERAPIST_AID_BRANDING.option);
            }
        },
    };
};

export const Settings = ({ logo, resized, preference }) => {
    return {
        loading: false,
        success: false,
        error: null,
        confirmDelete: false,

        logo,
        resized,
        preference,

        init() {
            this.$watch('success', (value) => value && setTimeout(() => this.success = false, 4000));
            this.$watch('error', (value) => value && setTimeout(() => this.error = null, 4000));
            this.$watch('preference', (value) => this.setPreference(value));
        },

        async setPreference(preference) {
            try {
                this.loading = true;

                await updateBrandingReference(preference);

                this.success = true;
                this.error = null;
            } catch (exception) {
                this.error = onFailure({ exception });
                this.success = false;
            } finally {
                this.loading = false;
            }
        },

        async deleteLogo() {
            try {
                this.loading = true;

                await axios.delete('/api/logo', {
                    withCredentials: true,
                    withXSRFToken: true,
                    headers: { 'Content-Type': 'multipart/form-data' },
                });

                this.resized = null;
                this.logo = null;
                this.confirmDelete = false;

                this.$dispatch('notify', { text: 'Logo successfully deleted!', type: 'info' });
            } catch (exception) {
                const message = onFailure({
                    exception,
                    defaultMessage: 'Something went wrong while deleting your logo. Please try again later.'
                });

                this.$dispatch('notify', {
                    title: 'Oops!',
                    text: message,
                    type: 'warning',
                });
            } finally {
                this.loading = false;

                const select = document.querySelector('#branding-preference');

                select.value === CUSTOM_BRANDING.option && this.updateDefaultLogoOption(THERAPIST_AID_BRANDING.option);
            }
        },

        refreshLogoOptions() {
            const select = document.querySelector('#branding-preference');

            select instanceof HTMLSelectElement && select.dispatchEvent(new CustomEvent('refresh-options'));
        },

        updateDefaultLogoOption(preference) {
            const select = document.querySelector('#branding-preference');

            if (select instanceof HTMLSelectElement) {
                select.dispatchEvent(new CustomEvent('update', { detail: preference || THERAPIST_AID_BRANDING.option }));
                select.dispatchEvent(new CustomEvent('refresh-options'));

                this.success = true;
            }
        },
    };
};
